import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Clock, Users, Edit2, Trash2, Plus, Undo2 } from 'lucide-react';
export const ActivityLogPanel = ({ isOpen, onClose, boardId, isDark, }) => {
    const [activeTab, setActiveTab] = useState('activity');
    const [activities, setActivities] = useState([]);
    const [lastViewed, setLastViewed] = useState([]);
    const [updates, setUpdates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [undoingIds, setUndoingIds] = useState(new Set());
    const [undoneActivities, setUndoneActivities] = useState(new Set());
    useEffect(() => {
        if (isOpen) {
            loadActivityData();
        }
    }, [isOpen, boardId]);
    const loadActivityData = async () => {
        setIsLoading(true);
        try {
            // Mock data - in production, fetch from Supabase
            // TODO: Replace with actual Supabase queries
            setActivities([
                {
                    id: '1',
                    type: 'create',
                    action: 'Created board',
                    user: { name: 'John Doe' },
                    timestamp: new Date(Date.now() - 86400000), // 1 day ago
                    description: 'Board "Marketing Sprint" was created',
                },
                {
                    id: '2',
                    type: 'update',
                    action: 'Updated item',
                    user: { name: 'Jane Smith' },
                    timestamp: new Date(Date.now() - 3600000), // 1 hour ago
                    description: 'Item "Design homepage mockups" status changed to "In Progress"',
                    itemId: 'item-1',
                },
                {
                    id: '3',
                    type: 'assign',
                    action: 'Assigned task',
                    user: { name: 'John Doe' },
                    timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
                    description: 'Assigned "Review designs" to Jane Smith',
                    itemId: 'item-2',
                },
            ]);
            setLastViewed([
                {
                    id: '1',
                    user: { name: 'John Doe' },
                    timestamp: new Date(Date.now() - 600000), // 10 minutes ago
                },
                {
                    id: '2',
                    user: { name: 'Jane Smith' },
                    timestamp: new Date(Date.now() - 3600000), // 1 hour ago
                },
            ]);
            setUpdates([
                {
                    id: '1',
                    type: 'schema',
                    description: 'Added new column "Priority"',
                    timestamp: new Date(Date.now() - 172800000), // 2 days ago
                    user: { name: 'John Doe' },
                },
                {
                    id: '2',
                    type: 'structure',
                    description: 'Created group "Backend Tasks"',
                    timestamp: new Date(Date.now() - 259200000), // 3 days ago
                    user: { name: 'Jane Smith' },
                },
            ]);
        }
        catch (error) {
            console.error('Failed to load activity data:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    const getActivityIcon = (type) => {
        switch (type) {
            case 'create':
                return _jsx(Plus, { className: "w-4 h-4" });
            case 'update':
                return _jsx(Edit2, { className: "w-4 h-4" });
            case 'delete':
                return _jsx(Trash2, { className: "w-4 h-4" });
            case 'comment':
                return _jsx(Edit2, { className: "w-4 h-4" });
            case 'assign':
                return _jsx(Users, { className: "w-4 h-4" });
            default:
                return _jsx(Clock, { className: "w-4 h-4" });
        }
    };
    const getActivityColor = (type) => {
        switch (type) {
            case 'create':
                return isDark ? '#10b981' : '#059669';
            case 'update':
                return isDark ? '#3b82f6' : '#2563eb';
            case 'delete':
                return isDark ? '#ef4444' : '#dc2626';
            case 'assign':
                return isDark ? '#f59e0b' : '#d97706';
            default:
                return isDark ? '#6b7280' : '#4b5563';
        }
    };
    const formatTime = (date) => {
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffMins < 1)
            return 'just now';
        if (diffMins < 60)
            return `${diffMins}m ago`;
        if (diffHours < 24)
            return `${diffHours}h ago`;
        if (diffDays < 7)
            return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };
    const handleUndo = async (activity) => {
        // Add to undoing set
        setUndoingIds(prev => new Set([...prev, activity.id]));
        try {
            // Simulate undo operation - in production, call Supabase function
            // to revert the activity based on its type
            await new Promise(resolve => setTimeout(resolve, 500));
            // Remove from activities list
            setActivities(prev => prev.filter(a => a.id !== activity.id));
            // Mark as undone
            setUndoneActivities(prev => new Set([...prev, activity.id]));
            // Show success message briefly
            setTimeout(() => {
                setUndoneActivities(prev => {
                    const next = new Set(prev);
                    next.delete(activity.id);
                    return next;
                });
            }, 2000);
            console.log(`[ActivityLog] Undid activity: ${activity.description}`);
        }
        catch (error) {
            console.error('[ActivityLog] Failed to undo activity:', error);
        }
        finally {
            setUndoingIds(prev => {
                const next = new Set(prev);
                next.delete(activity.id);
                return next;
            });
        }
    };
    const filteredActivities = activities.filter(activity => {
        const matchesSearch = activity.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterType === 'all' || activity.type === filterType;
        return matchesSearch && matchesFilter;
    });
    if (!isOpen)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 z-40", onClick: onClose }), _jsxs("div", { className: "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[80vh] rounded-lg shadow-xl z-50 overflow-hidden border flex flex-col", style: {
                    backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                    borderColor: isDark ? '#333333' : '#e5e7eb',
                }, children: [_jsxs("div", { className: "flex items-center justify-between p-6 border-b", style: {
                            borderColor: isDark ? '#333333' : '#e5e7eb',
                            backgroundColor: isDark ? '#0f1d2e' : '#f9fafb',
                        }, children: [_jsx("h2", { className: "text-xl font-bold", style: { color: isDark ? '#ffffff' : '#000000' }, children: "Activity Log" }), _jsx("button", { onClick: onClose, className: "p-2 rounded-lg hover:bg-opacity-50 transition-colors", style: {
                                    backgroundColor: isDark ? '#333333' : '#e5e7eb',
                                }, children: _jsx(X, { className: "w-5 h-5", style: { color: isDark ? '#ffffff' : '#000000' } }) })] }), _jsx("div", { className: "flex border-b", style: {
                            borderColor: isDark ? '#333333' : '#e5e7eb',
                        }, children: [
                            { id: 'activity', label: 'Activity' },
                            { id: 'lastViewed', label: 'Last viewed' },
                            { id: 'updates', label: 'Updates' },
                        ].map(tab => (_jsx("button", { onClick: () => setActiveTab(tab.id), className: `flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id
                                ? isDark
                                    ? 'text-[#0ea5e9] border-[#0ea5e9]'
                                    : 'text-[#00A5CF] border-[#00A5CF]'
                                : isDark
                                    ? 'text-gray-400 border-transparent hover:text-gray-300'
                                    : 'text-gray-600 border-transparent hover:text-gray-700'}`, style: {
                                borderColor: activeTab === tab.id
                                    ? isDark ? '#0ea5e9' : '#00A5CF'
                                    : 'transparent',
                            }, children: tab.label }, tab.id))) }), _jsxs("div", { className: "flex-1 overflow-y-auto p-6 space-y-4", children: [activeTab === 'activity' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "space-y-3 mb-6", children: [_jsx("input", { type: "text", placeholder: "Search activities...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full px-3 py-2 rounded-lg border outline-none transition-colors", style: {
                                                    backgroundColor: isDark ? '#2a2a2a' : '#f9fafb',
                                                    borderColor: isDark ? '#444444' : '#d1d5db',
                                                    color: isDark ? '#ffffff' : '#000000',
                                                } }), _jsxs("select", { value: filterType, onChange: (e) => setFilterType(e.target.value), className: "w-full px-3 py-2 rounded-lg border outline-none transition-colors", style: {
                                                    backgroundColor: isDark ? '#2a2a2a' : '#f9fafb',
                                                    borderColor: isDark ? '#444444' : '#d1d5db',
                                                    color: isDark ? '#ffffff' : '#000000',
                                                }, children: [_jsx("option", { value: "all", children: "All activities" }), _jsx("option", { value: "create", children: "Created" }), _jsx("option", { value: "update", children: "Updated" }), _jsx("option", { value: "assign", children: "Assigned" }), _jsx("option", { value: "delete", children: "Deleted" })] })] }), isLoading ? (_jsx("div", { className: "text-center py-8", style: { color: isDark ? '#888888' : '#6b7280' }, children: "Loading activities..." })) : filteredActivities.length > 0 ? (filteredActivities.map(activity => (_jsx("div", { className: "p-4 rounded-lg border", style: {
                                            backgroundColor: isDark ? '#0f1d2e' : '#f9fafb',
                                            borderColor: isDark ? '#333333' : '#e5e7eb',
                                        }, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "p-2 rounded-lg flex-shrink-0", style: { backgroundColor: getActivityColor(activity.type) + '20' }, children: _jsx("div", { style: { color: getActivityColor(activity.type) }, children: getActivityIcon(activity.type) }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("p", { className: "font-medium", style: { color: isDark ? '#ffffff' : '#000000' }, children: activity.user.name }), _jsx("p", { className: "text-xs", style: { color: isDark ? '#888888' : '#6b7280' }, children: formatTime(activity.timestamp) })] }), _jsx("p", { className: "text-sm mt-1", style: { color: isDark ? '#bbbbbb' : '#4b5563' }, children: activity.description })] }), _jsx("button", { onClick: () => handleUndo(activity), disabled: undoingIds.has(activity.id), className: "flex-shrink-0 p-2 rounded-lg hover:bg-opacity-70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed", style: {
                                                        backgroundColor: isDark ? '#333333' : '#e5e7eb',
                                                        color: isDark ? '#ffffff' : '#000000',
                                                    }, title: "Undo this activity", children: _jsx(Undo2, { className: `w-4 h-4 ${undoingIds.has(activity.id) ? 'animate-spin' : ''}` }) })] }) }, activity.id)))) : (_jsx("div", { className: "text-center py-8", style: { color: isDark ? '#888888' : '#6b7280' }, children: "No activities found" }))] })), activeTab === 'lastViewed' && (_jsx(_Fragment, { children: lastViewed.length > 0 ? (lastViewed.map(entry => (_jsx("div", { className: "p-4 rounded-lg border flex items-center justify-between", style: {
                                        backgroundColor: isDark ? '#0f1d2e' : '#f9fafb',
                                        borderColor: isDark ? '#333333' : '#e5e7eb',
                                    }, children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold", style: {
                                                    backgroundColor: isDark ? '#0ea5e9' : '#00A5CF',
                                                    color: '#ffffff',
                                                }, children: entry.user.name.charAt(0).toUpperCase() }), _jsxs("div", { children: [_jsx("p", { className: "font-medium", style: { color: isDark ? '#ffffff' : '#000000' }, children: entry.user.name }), _jsxs("p", { className: "text-sm", style: { color: isDark ? '#888888' : '#6b7280' }, children: ["Last viewed ", formatTime(entry.timestamp)] })] })] }) }, entry.id)))) : (_jsx("div", { className: "text-center py-8", style: { color: isDark ? '#888888' : '#6b7280' }, children: "No view history available" })) })), activeTab === 'updates' && (_jsx(_Fragment, { children: updates.length > 0 ? (updates.map(update => (_jsx("div", { className: "p-4 rounded-lg border", style: {
                                        backgroundColor: isDark ? '#0f1d2e' : '#f9fafb',
                                        borderColor: isDark ? '#333333' : '#e5e7eb',
                                    }, children: _jsx("div", { className: "flex items-start justify-between", children: _jsxs("div", { children: [_jsx("p", { className: "font-medium", style: { color: isDark ? '#ffffff' : '#000000' }, children: update.description }), _jsxs("p", { className: "text-sm mt-1", style: { color: isDark ? '#888888' : '#6b7280' }, children: ["by ", update.user.name, " \u2022 ", formatTime(update.timestamp)] })] }) }) }, update.id)))) : (_jsx("div", { className: "text-center py-8", style: { color: isDark ? '#888888' : '#6b7280' }, children: "No board updates" })) }))] })] })] }));
};
